/**
 * The name-sticker sheet ladder: a cart-wide quantity discount over every
 * Standard and Mix sheet in the order (spec #107, DEC-0036).
 *
 * Shopify owns the numbers, the app owns the arithmetic (DEC-0035 extended).
 * The ladder is a set of native automatic discounts — one per order size N,
 * each a fixed amount `f(N)` off the whole order once N eligible sheets are in
 * the cart — read over the Admin API and translated into `SheetLadderTier`s by
 * `integrations/shopify/name-sticker-ladder-discounts.ts`. Nothing here knows
 * that the live table happens to be `min(60, 10k + 20)` kr per sheet; that
 * formula lives only in test expectations. Everything is derived from the
 * table, so retuning a discount in the admin retunes every surface.
 *
 * This is the only place ladder prices are computed. The configurator's
 * quantity step, panel and mobile bar, the add-to-cart popup, the cart page
 * and the phone upsell all read a `SheetLadderQuote` instead of subtracting
 * anything themselves.
 *
 * Why the total is independent of add order and sheet type: `f(N)` depends on
 * the count alone, and the discount on sheet k is `f(k) − f(k−1)` whichever
 * sheet type happens to be k-th. Standard and Mix sheets therefore earn the
 * same kronor at the same position, and any split of the same sheets between
 * "already in the cart" and "being added" sums to the same `f(N)`.
 *
 * Pure and client-safe: no integration imports, so client components can use
 * it directly. All amounts are integer öre.
 */

import { isOfferedSingleSizeId } from "./size-contract";
import type { SizeId } from "./types";

/** A single-size sheet, as far as ladder eligibility is concerned. */
export interface SingleSheetLadderSubject {
  kind: "single";
  size: SizeId;
}

/** A Mix sheet, as far as ladder eligibility is concerned. */
export interface MixSheetLadderSubject {
  kind: "mix";
}

/**
 * The slice of a sheet that decides whether it counts toward the ladder. A
 * `StickerConfiguration` satisfies it, and so does a catalog entry mapped to
 * `{ kind, size }`.
 */
export type SheetLadderSubject = SingleSheetLadderSubject | MixSheetLadderSubject;

/**
 * Whether a sheet counts toward the ladder's N and earns its discount: every
 * Mix sheet, and the single size the selector offers (Standard). Legacy
 * Small/Large sheets, still reachable through edit and reorder links, sit
 * outside the ladder's discounts.
 *
 * The one statement of the rule. The configurator's live price and popup
 * hint, the eligible-variant set the discounts are held against, and the
 * `order_paid` summary all ask here.
 */
export function isLadderEligible(sheet: SheetLadderSubject): boolean {
  return sheet.kind === "mix" || isOfferedSingleSizeId(sheet.size);
}

/** One automatic discount in the ladder. */
export interface SheetLadderTier {
  /** Eligible sheets the order must hold for this discount to apply. */
  minQuantity: number;
  /** Fixed amount off the whole order, in öre — `f(minQuantity)`. */
  amountOff: number;
}

/** The ladder as the app knows it: the tier table plus which sheets count. */
export interface SheetLadder {
  /** Sorted by `minQuantity`, strictly increasing in both fields. */
  tiers: readonly SheetLadderTier[];
  /**
   * Shopify variant GIDs whose sheets count toward N — the Standard single
   * and the Mix variants. Legacy Small/Large lines (still reachable through
   * reorder links) sit outside the discount and must not count.
   */
  eligibleVariantIds: readonly string[];
}

export const EMPTY_SHEET_LADDER: SheetLadder = { tiers: [], eligibleVariantIds: [] };

/**
 * The order-wide ladder discount at N eligible sheets, in öre — the best tier
 * the count qualifies for, as Shopify applies the best qualifying discount.
 * A gap in the table (a deleted N = 5 discount) therefore reads as sheet 5 at
 * ordinary price and sheet 6 taking both steps, exactly as Shopify charges it.
 */
export function sheetLadderDiscount(tiers: readonly SheetLadderTier[], sheetCount: number): number {
  let amountOff = 0;

  for (const tier of tiers) {
    if (sheetCount >= tier.minQuantity && tier.amountOff > amountOff) {
      amountOff = tier.amountOff;
    }
  }

  return amountOff;
}

/** Sheet count from which further sheets cost ordinary price, or `null` without a ladder. */
export function sheetLadderCap(tiers: readonly SheetLadderTier[]): number | null {
  if (tiers.length === 0) {
    return null;
  }

  return Math.max(...tiers.map((tier) => tier.minQuantity));
}

export interface LadderSheetPrice {
  /** 1-based position of the sheet in the order's eligible sheets. */
  position: number;
  /** What this sheet adds to the order total, in öre. */
  price: number;
  /** The variant's price without the ladder, in öre. */
  ordinaryPrice: number;
}

/** Whether the ladder takes anything off this sheet — the "(ord. X kr)" / "endast" test. */
export function isLadderDiscounted(sheet: Pick<LadderSheetPrice, "price" | "ordinaryPrice">): boolean {
  return sheet.price < sheet.ordinaryPrice;
}

/** A run of consecutive positions that cost the same, for the "1:a arket …" line. */
export interface SheetLadderPositionBand {
  fromPosition: number;
  /** Inclusive. Equal to `fromPosition` for a single-position band. */
  toPosition: number;
  price: number;
  ordinaryPrice: number;
}

export interface SheetLadderQuoteInput {
  tiers: readonly SheetLadderTier[];
  /**
   * Eligible sheets already in the cart, excluding the line being edited —
   * see `countLadderSheets`.
   */
  sheetsInCart: number;
  /** Sheets this action adds. `0` asks "what would the next one cost?". */
  sheetsAdding: number;
  /** Shopify's live variant `price` (never compare-at) of the type being added, in öre. */
  baseUnitPrice: number;
}

export interface SheetLadderQuote {
  /** One entry per added sheet, in position order. */
  sheets: LadderSheetPrice[];
  /** What the added sheets cost with the ladder, in öre. */
  total: number;
  /** What they cost without it, in öre. */
  ordinaryTotal: number;
  /** `ordinaryTotal − total`, in öre. */
  savings: number;
  /** The sheet after the added ones — the "+1 ark = Y kr" hint. */
  nextSheet: LadderSheetPrice;
  /** True once the order reaches the top tier: further sheets cost ordinary price. */
  capReached: boolean;
  /**
   * The order-wide ladder discount Shopify should grant once the added sheets
   * are in the cart, in öre — `f(sheetsInCart + sheetsAdding)`. What the cart
   * summary shows and what the drift check compares against.
   */
  orderDiscount: number;
  /**
   * Price bands from the first added position up to the cap, consecutive equal
   * prices merged. Empty without a ladder, or once the cart is past the cap.
   */
  bands: SheetLadderPositionBand[];
}

function toCount(value: number): number {
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function priceAtPosition(
  tiers: readonly SheetLadderTier[],
  position: number,
  baseUnitPrice: number
): LadderSheetPrice {
  const step = sheetLadderDiscount(tiers, position) - sheetLadderDiscount(tiers, position - 1);

  return {
    position,
    // A step larger than the sheet itself cannot be charged — Shopify never
    // takes a line below zero — so the displayed price floors there too.
    price: Math.max(0, baseUnitPrice - step),
    ordinaryPrice: baseUnitPrice,
  };
}

/**
 * Price the sheets being added against the cart. With an empty or unreadable
 * table every sheet is at ordinary price, savings are 0 and the cap is never
 * reached — the "no ladder" degradation.
 */
export function quoteSheetLadder({
  tiers,
  sheetsInCart,
  sheetsAdding,
  baseUnitPrice,
}: SheetLadderQuoteInput): SheetLadderQuote {
  const inCart = toCount(sheetsInCart);
  const adding = toCount(sheetsAdding);
  const orderSheets = inCart + adding;

  const sheets: LadderSheetPrice[] = [];
  for (let position = inCart + 1; position <= orderSheets; position += 1) {
    sheets.push(priceAtPosition(tiers, position, baseUnitPrice));
  }

  const total = sheets.reduce((sum, sheet) => sum + sheet.price, 0);
  const ordinaryTotal = baseUnitPrice * adding;

  const cap = sheetLadderCap(tiers);
  const bands: SheetLadderPositionBand[] = [];
  if (cap !== null) {
    for (let position = inCart + 1; position <= cap; position += 1) {
      const { price, ordinaryPrice } = priceAtPosition(tiers, position, baseUnitPrice);
      const previous = bands[bands.length - 1];
      if (previous && previous.price === price) {
        previous.toPosition = position;
      } else {
        bands.push({ fromPosition: position, toPosition: position, price, ordinaryPrice });
      }
    }
  }

  return {
    sheets,
    total,
    ordinaryTotal,
    savings: ordinaryTotal - total,
    nextSheet: priceAtPosition(tiers, orderSheets + 1, baseUnitPrice),
    capReached: cap !== null && orderSheets >= cap,
    orderDiscount: sheetLadderDiscount(tiers, orderSheets),
    bands,
  };
}

/** The variant a countable line is for. */
export interface LadderCountableMerchandise {
  /** Shopify variant GID. */
  id: string;
}

/** The structural slice of a cart line the count needs. */
export interface LadderCountableLine {
  id: string;
  quantity: number;
  merchandise: LadderCountableMerchandise;
}

export interface CountLadderSheetsOptions {
  /** The line being edited — it is about to be replaced, so it must not count twice. */
  excludeLineId?: string | null;
}

/**
 * Eligible sheets in a cart: the summed quantity of lines on a ladder
 * variant. Never `cart.totalQuantity`, which also counts legacy sizes and any
 * other product.
 */
export function countLadderSheets(
  lines: readonly LadderCountableLine[],
  eligibleVariantIds: readonly string[],
  { excludeLineId = null }: CountLadderSheetsOptions = {}
): number {
  const eligible = new Set(eligibleVariantIds);

  return lines.reduce((count, line) => {
    if (line.id === excludeLineId || !eligible.has(line.merchandise.id)) {
      return count;
    }
    return count + toCount(line.quantity);
  }, 0);
}
